import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/providers/AuthProvider';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import type { ClientMessage } from '@/types/database';

const POLL_INTERVAL_MS = 15_000;

export function useMessages() {
  const { clientId } = useCurrentClient();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['client_messages', clientId],
    enabled: !!clientId,
    staleTime: 5_000,
    refetchInterval: POLL_INTERVAL_MS,
    queryFn: async (): Promise<ClientMessage[]> => {
      if (!clientId) return [];
      const { data, error } = await supabase
        .from('client_messages')
        .select('*')
        .eq('client_id', clientId)
        .order('created_at', { ascending: true })
        .limit(500);
      if (error) throw error;
      return (data ?? []) as ClientMessage[];
    },
  });

  // Marca como lidas as mensagens recebidas (não enviadas pelo usuário atual).
  const { user } = useAuth();
  const messages = query.data;

  useEffect(() => {
    if (!user?.id || !clientId || !messages || messages.length === 0) return;
    const unreadFromOthers = messages.filter(
      (m) => m.sender_user_id !== user.id && m.read_at === null,
    );
    if (unreadFromOthers.length === 0) return;

    const ids = unreadFromOthers.map((m) => m.id);
    void supabase
      .from('client_messages')
      .update({ read_at: new Date().toISOString() })
      .in('id', ids)
      .then(() => {
        qc.invalidateQueries({ queryKey: ['client_messages', clientId] });
      });
  }, [messages, user?.id, clientId, qc]);

  return query;
}

export function useUnreadMessageCount() {
  const { clientId } = useCurrentClient();
  const { user } = useAuth();
  return useQuery({
    queryKey: ['client_messages', clientId, 'unread', user?.id],
    enabled: !!clientId && !!user?.id,
    staleTime: 10_000,
    refetchInterval: POLL_INTERVAL_MS,
    queryFn: async (): Promise<number> => {
      if (!clientId || !user?.id) return 0;
      const { count, error } = await supabase
        .from('client_messages')
        .select('id', { count: 'exact', head: true })
        .eq('client_id', clientId)
        .is('read_at', null)
        .neq('sender_user_id', user.id);
      if (error) return 0;
      return count ?? 0;
    },
  });
}

export function useSendMessage() {
  const qc = useQueryClient();
  const { user, profile } = useAuth();
  const { clientId } = useCurrentClient();
  return useMutation({
    mutationFn: async (body: string) => {
      if (!clientId) throw new Error('Cliente não selecionado');
      if (!user?.id) throw new Error('Não autenticado');
      const trimmed = body.trim();
      if (!trimmed) throw new Error('Mensagem vazia');

      const { error } = await supabase.from('client_messages').insert({
        client_id: clientId,
        sender_user_id: user.id,
        sender_name: profile?.name || user.email || 'Anônimo',
        sender_role: profile?.role ?? 'cliente',
        body: trimmed,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['client_messages'] });
    },
    onError: (e: Error) => toast.error(`Erro ao enviar: ${e.message}`),
  });
}
